<template>
  <div class="dashboard scrollable-dashboard">
    <!-- Timeline -->
    <Timeline ref="timeLine" :method-being-called="getAllData"/>

    <!-- HEADER -->
    <div class="dashboard-header">
      <h2>Franchise Performance Dashboard</h2>
      <div class="dashboard-actions">
          <button class="btn btn-success interactive-btn" @click="exportDashboardToExcel">
            <font-awesome-icon :icon="['fas', 'table']"/>
            <span class="btn-text">Excel</span>
          </button>
          <button class="btn refresh interactive-btn" @click="getAllData">
            <font-awesome-icon :icon="['fas', 'sync']"/>
            <span class="btn-text">Refresh</span>
          </button>
        </div>
    </div>

    <!-- Android permission prompt -->
    <div v-if="showAsk" class="notice" role="dialog" aria-live="polite">
      <p class="notice-text">Allow notifications for reminders and updates?</p>
      <div class="notice-actions">
        <button class="btn interactive-btn" @click="enableNotifications">
          <font-awesome-icon :icon="['fas', 'check']"/>
          <span class="btn-text">Allow</span>
        </button>
        <button class="btn interactive-btn" @click="dismissAsk">
          <font-awesome-icon :icon="['fas', 'times']"/>
          <span class="btn-text">Not now</span>
        </button>
      </div>
    </div>

    <!-- KPI OVERVIEW -->
    <div class="dashboard-kpis">
      <KpiCard
        icon="💰"
        title="Revenue (excl VAT)"
        :value="$utils.formatDefaultCurrency(revenue.salesTotal)"
        :numericValue="parseFloat(revenue.salesTotal)"
        :thresholds="{ low: 10000, medium: 50000, high: 100000 }"
        card-class="bg-gradient-success"
        use-gradient
        @click="exportRevenueCostDetails"
      />
      <KpiCard
        icon="🧾"
        title="Cost of Sales (excl VAT)"
        :value="$utils.formatDefaultCurrency(revenue.costOfSales)"
        :numericValue="parseFloat(revenue.costOfSales)"
        :thresholds="{ low: 5000, medium: 20000, high: 50000 }"
        :inverse="true"
        card-class="bg-gradient-warning"
        use-gradient
        @click="exportRevenueCostDetails"
      />
      <KpiCard
        icon="💸"
        title="Expenses"
        :value="$utils.formatDefaultCurrency(revenue.expenseValue)"
        :numericValue="parseFloat(revenue.expenseValue)"
        :thresholds="{ low: 5000, medium: 20000, high: 50000 }"
        :inverse="true"
        card-class="bg-gradient-warning"
        use-gradient
        @click="exportExpensesDetails"
      />
      <KpiCard
        icon="📈"
        title="Net Profit"
        :value="$utils.formatDefaultCurrency(netProfit)"
        :numericValue="netProfit"
        :thresholds="{ low: 5000, medium: 20000, high: 50000 }"
      />
    </div>

    <!-- POSITIVE / NEGATIVE POSITION -->
    <div class="financial-position">
      <div class="position-block">
        <h5>Positive Position</h5>
        <ul>
          <li @click="exportStockValue">
            <label>Stock Value</label>
            <span :class="$colorIndicator.getValueColorClass(parseFloat(positivePosition.stockValue), 10000, 50000, 100000)">
              {{ $utils.formatDefaultCurrency(positivePosition.stockValue) }}
            </span>
          </li>
          <li @click="exportCustomerList">
            <label>Debtors</label>
            <span :class="$colorIndicator.getValueColorClass(parseFloat(positivePosition.debtorBalance), 5000, 20000, 50000)">
              {{ $utils.formatDefaultCurrency(positivePosition.debtorBalance) }}
            </span>
          </li>
          <li @click="exportInputVat">
            <label>Input VAT</label>
            <span :class="$colorIndicator.getValueColorClass(parseFloat(positivePosition.inputVatTotal), 1000, 5000, 10000)">
              {{ $utils.formatDefaultCurrency(positivePosition.inputVatTotal) }}
            </span>
          </li>
        </ul>
      </div>
      <div class="position-block">
        <h5>Negative Position</h5>
        <ul>
          <li @click="exportSupplierList">
            <label>Supplier Balance</label>
            <span :class="$colorIndicator.getValueColorClass(parseFloat(negativePosition.supplierBalance), 0, 10000, 30000, true)">
              {{ $utils.formatDefaultCurrency(negativePosition.supplierBalance) }}
            </span>
          </li>
          <li @click="exportOutputVat">
            <label>Output VAT</label>
            <span :class="$colorIndicator.getValueColorClass(parseFloat(negativePosition.outPutVat), 0, 5000, 10000, true)">
              {{ $utils.formatDefaultCurrency(negativePosition.outPutVat) }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <!-- CHARTS -->
    <div class="charts-container">
      <!-- Bar Chart: Franchise Sales -->
      <div class="chart-card">
        <div class="chart-header">
          <h5 class="chart-title">Franchise Sales</h5>
          <div class="chart-actions">
            <button class="btn view interactive-btn" @click="openSalesReports">
              <font-awesome-icon :icon="['fas', 'eye']"/>
              View
            </button>

            <button class="btn interactive-btn" @click="printFranchiseTurnoverPDF">
              <font-awesome-icon :icon="['fas', 'print']"/>
              PDF
            </button>

            <button class="btn excel interactive-btn" @click="exportCostOfSales">
              <font-awesome-icon :icon="['fas', 'file-excel']"/>
              Cost of Sales
            </button>

            <button class="btn excel interactive-btn" @click="exportFranchiseSales">
              <font-awesome-icon :icon="['fas', 'table']"/>
              Excel
            </button>
          </div>
        </div>
        <BarChart :chart-data="BarChartData"/>
      </div>


      <!-- Bar Chart: Purchases -->
      <div class="chart-card">
        <div class="chart-header">
          <h5 class="chart-title">Franchise Stock Purchases</h5>
          <div class="chart-actions">
            <button class="btn view interactive-btn" @click="openStockReports">
              <font-awesome-icon :icon="['fas', 'eye']"/>
              View
            </button>

            <button class="btn interactive-btn" @click="printFranchiseTurnoverPDF">
              <font-awesome-icon :icon="['fas', 'print']"/>
              PDF
            </button>
            <button class="btn excel interactive-btn" @click="exportFranchisePurchases">
              <font-awesome-icon :icon="['fas', 'table']"/>
              Excel
            </button>
          </div>
        </div>
        <BarChart :chart-data="Bar2ChartData"/>
      </div>
    </div>
  </div>
</template>

<script>
import Timeline from "@/utils/TimelineSelection/Timeline.vue";
import BarChart from "@/components/charts/BarChart.js";
import PieChart from "@/components/charts/Pie.js";
import KpiCard from "@/components/layout/utils/KPICard";
import {utils as $utils} from "@/Services/utils";
import * as XLSX from "xlsx";
import { openLocalXlsx } from "@/Services/docOpeners";
import Swal from "sweetalert2";
import { NotificationService, isCapacitor } from "@/Services/NotificationService";

export default {
  name: "Dashboard",
  components: {
    Timeline,
    BarChart,
    PieChart,
    KpiCard
  },
  data() {
    return {
      revenue: {},
      positivePosition: {},
      negativePosition: {},
      stores: [],
      purchaseData: [],
      showAsk: false,
      BarChartData: {
        labels: [],
        datasets: [],
      },
      Bar2ChartData: {
        labels: [],
        datasets: [],
      },
      chartData: {
        labels: [],
        datasets: [
          {
            backgroundColor: [
              "#419883", "#E46651", "#00D8FF", "#DD1B16", "#115845",
              "#775566", "#721266", "#275856", "#721206", "#275866"
            ],
            data: [],
          },
        ],
      },
    };
  },
  computed: {
    hasData() {
      return this.chartData?.datasets?.some(dataset =>
          dataset.data?.some(value => value > 0)
      );
    },
    $utils() {
      return $utils;
    },
    currentStore() {
      return this.$store.getters.getStore;
    },
    netProfit() {
      const gross = parseFloat(this.revenue?.grossProfit) || 0;
      const expenses = parseFloat(this.revenue?.expenseValue) || 0;
      return gross - expenses;
    },
  },
  watch: {
    currentStore(newStore, oldStore) {
      if (newStore.StoreDB !== oldStore.StoreDB) {
        this.getAllData();
      }
    },
  },
  async mounted() {
    this.getAllData();
    // Android permission gate: ask only on Capacitor if not granted yet
    try {
      if (isCapacitor) {
        const status = await NotificationService.getPermissionStatus();
        this.showAsk = status !== 'granted';
      }
    } catch {}
  },
  methods: {
    // KPI card click handlers
    showRevenueDetails() {
      this.$hapticFeedback.success();
      Toast.fire({
        icon: 'success',
        title: 'Revenue Details',
        text: `Total revenue (excl VAT): ${this.$utils.formatDefaultCurrency(this.revenue.salesTotal)}`,
        timer: 3000
      });
    },

    showExpensesDetails() {
      this.$hapticFeedback.warning();
      Toast.fire({
        icon: 'warning',
        title: 'Expenses Details',
        text: `Total expenses: ${this.$utils.formatDefaultCurrency(this.revenue.expenseValue)}`,
        timer: 3000
      });
    },

    showProfitDetails() {
      this.$hapticFeedback.success();
      Toast.fire({
        icon: 'success',
        title: 'Profit Details',
        text: `Net profit: ${this.$utils.formatDefaultCurrency(this.netProfit)}`,
        timer: 3000
      });
    },

    showPositionDetails(type, value, isPositive) {
      let title = '';
      let icon = isPositive ? 'success' : 'warning';

      switch(type) {
        case 'stockValue':
          title = 'Stock Value';
          break;
        case 'debtorBalance':
          title = 'Debtor Balance';
          break;
        case 'inputVatTotal':
          title = 'Input VAT';
          break;
        case 'supplierBalance':
          title = 'Supplier Balance';
          break;
        case 'outPutVat':
          title = 'Output VAT';
          break;
      }

      this.$hapticFeedback.medium();
      Toast.fire({
        icon: icon,
        title: title,
        text: `${title}: ${this.$utils.formatDefaultCurrency(value)}`,
        timer: 3000
      });
    },

    async getAllData() {
      try {
        this.$utils.showLoading();
        const results = await Promise.allSettled([
          this.getTurnOverData(),
          this.getFranchiseRevenue(),
          this.getPositivePosition(),
          this.getNegativePosition(),
          this.getTopCategories(),
          this.getFranchisePurchases(),
        ]);

        let hasErrors = false;
        results.forEach((result, index) => {
          if (result.status === "rejected") {
            console.error(`Promise ${index} failed:`, result.reason);
            hasErrors = true;
          }
        });

        if (hasErrors) {
          this.$hapticFeedback.warning();
          Toast.fire({
            icon: 'warning',
            title: 'Some Data Failed to Load',
            text: 'Some dashboard data could not be loaded. You may see partial information.',
            timer: 3000
          });
        } else {
          this.$hapticFeedback.success();
          Toast.fire({
            icon: 'success',
            title: 'Dashboard Loaded',
            timer: 1000
          });
        }
      } catch (e) {
        console.error("Data fetch failed", e);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Error Loading Dashboard',
          text: 'An unexpected error occurred while loading the dashboard.',
          timer: 3000
        });
      } finally {
        this.$utils.hideLoading();
      }
    }, 

    exportCostOfSales() {
      this.$hapticFeedback.medium();

      try {
        const rows = (this.stores || []).map(ft => ({
          'Store': ft.store,
          'Cost Of Sales (excl VAT)': Number(ft.costOfSales) || 0
        }));

        if (rows.length === 0) {
          this.$hapticFeedback.error();
          Toast.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'No cost of sales data available to export.',
            timer: 3000
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CostOfSales");

        const ab = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab, "Cost of Sales.xlsx");

        this.$hapticFeedback.success();
        Toast.fire({
          icon: 'success',
          title: 'Export Successful',
          text: 'Cost of sales exported to Excel.',
          timer: 2000
        });
      } catch (error) {
        console.error('Error exporting cost of sales:', error);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'Failed to export cost of sales. Please try again.',
          timer: 3000
        });
      }
    },

    async exportRevenueCostDetails() {
      this.$hapticFeedback.medium();
      this.$utils.showLoading();
      try {
        const rows = await this.$store.dispatch('getCompletedSalesDetails');
        const grouped = this.normalizeCompletedSales(rows || []);
        if (!grouped.length) {
          Toast.fire({
            icon: 'warning',
            title: 'No invoice data found for the selected range'
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(grouped);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'RevenueCost');
        const ab = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab, 'Revenue_and_CostOfSales.xlsx');

        Toast.fire({ icon: 'success', title: 'Exported Revenue & Cost of Sales' });
      } catch (e) {
        console.error('Failed to export revenue/cost details', e);
        Toast.fire({
          icon: 'error',
          title: 'Export failed',
          text: 'Could not fetch invoice details. Please try again.'
        });
      } finally {
        this.$utils.hideLoading();
      }
    },

    async exportExpensesDetails() {
      this.$hapticFeedback.medium();
      this.$utils.showLoading();
      try {
        const res = await this.$store.dispatch('getFinanceData', {
          route: 'finance/income',
          query: { limit: 1000 }
        });
        const expenses = res?.expenses ?? [];

        // Resolve store name from localStorage (matching buildExportContext() pattern)
        const ctx = this.buildExportContext();
        let storeName = '-';
        if (ctx.schema) {
          const storesList = JSON.parse(localStorage.getItem("stores") || "[]");
          const found = storesList.find(s => s.StoreDB === ctx.schema);
          storeName = found ? found.StoreName : '-';
        }

        const normalized = expenses.map(item => ({
          Store: storeName,
          User: '-',
          Reference: '-',
          Type: item.acc_Name ?? '-',
          Supplier: '-',
          Amount: Number(item.amount ?? 0),
          Date: res?.from && res?.to ? `${res.from} to ${res.to}` : '-'
        }));

        if (!normalized.length) {
          Toast.fire({
            icon: 'warning',
            title: 'No expenses found for the selected range'
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(normalized);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
        const ab = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab, 'Expenses.xlsx');

        Toast.fire({ icon: 'success', title: 'Exported Expenses' });
      } catch (e) {
        console.error('Failed to export expenses', e);
        Toast.fire({
          icon: 'error',
          title: 'Export failed',
          text: 'Could not fetch expenses. Please try again.'
        });
      } finally {
        this.$utils.hideLoading();
      }
    },

    // Export actions for position cards
    buildExportContext() {
      const stores = JSON.parse(localStorage.getItem("stores") || "[]");
      const store = JSON.parse(localStorage.getItem("selected_store") || "null");
      const tf = this.$store.getters.getTimeFrame || {};
      return {
        isAll: store && store.StoreName === "All",
        schema: store?.StoreDB,
        group: stores[0]?.Group,
        from: tf.from,
        to: tf.to,
      };
    },
    exportStockValue() {
      const ctx = this.buildExportContext();
      if (!ctx.schema && !ctx.isAll) {
        return responses.showWarning("Select a store first");
      }
      this.$store.dispatch("printStockPDF", {
        route: "stock/export/pdf-report/stock-value",
        fileName: "Stock Value",
      });
    },
    exportSupplierList() {
      const ctx = this.buildExportContext();
      if (!ctx.schema) return responses.showWarning("Select a store first");
      const url = `${window.docsApi}creditor/${ctx.schema}/exportSupplierList?Limit=500`;
      const headers = ctx.group ? { group: window.btoa(ctx.group) } : {};
      this.$axios
        .get(url, { headers, responseType: "blob" })
        .then((res) => openFile(res, "Supplier List"))
        .catch((err) => {
          console.error(err);
          responses.showError("Failed to download supplier list");
        });
    },
    exportCustomerList() {
      const ctx = this.buildExportContext();
      if (!ctx.schema) return responses.showWarning("Select a store first");
      const url = `${window.docsApi}tally/${ctx.schema}/customerReceipts?from=${ctx.from}&to=${ctx.to}`;
      const headers = ctx.group ? { group: window.btoa(ctx.group) } : {};
      this.$axios
        .get(url, { headers, responseType: "blob" })
        .then((res) => openFile(res, "Customer Receipts"))
        .catch((err) => {
          console.error(err);
          responses.showError("Failed to download customer receipts");
        });
    },
    exportOutputVat() {
      const ctx = this.buildExportContext();
      const url =
        ctx.isAll || !ctx.schema
          ? `${window.ApiServer}PDFReport/FranchiseVATSummary/${ctx.group}?from=${ctx.from}&to=${ctx.to}`
          : `${window.ApiServer}PDFReport/VATSummary/${ctx.schema}?from=${ctx.from}&to=${ctx.to}`;
      window.open(url, "_blank");
      this.$hapticFeedback.medium();
    },
    exportInputVat() {
      const ctx = this.buildExportContext();
      const url =
        ctx.isAll || !ctx.schema
          ? `${window.ApiServer}FranchiseVAT/InputVAT/${ctx.group}?from=${ctx.from}&to=${ctx.to}`
          : `${window.ApiServer}VAT/InputVAT/${ctx.schema}?from=${ctx.from}&to=${ctx.to}`;
      window.open(url, "_blank");
      this.$hapticFeedback.medium();
    },

    normalizeCompletedSales(rows) {
      const asNumber = (v) => {
        const n = Number(v);
        return isNaN(n) ? 0 : n;
      };
      const grouped = new Map();
      for (const r of rows) {
        const inv = r.invnum || r.invNum || r.Invnum || r.invoice || r.Invoice || r.invoiceNumber || r.InvoiceNumber;
        if (!inv) continue;
        const key = String(inv);
        const entry = grouped.get(key) || {
          Store: r.Store ?? r.store,
          Invnum: key,
          PaymentType: r.PaymentType ?? r.paymentType ?? r.payType ?? '',
          User: r.User ?? r.user ?? r.username ?? '',
          PC: r.PC ?? r.pc ?? r.till ?? r.register ?? '',
          Customer: r.Customer ?? r.customer ?? r.custName ?? '',
          TotalVAT: 0,
          CostOfSales: 0,
          SaleTotal: 0,
          Qty: 0,
          Date: r.date || r.createdAt || r.tranDate || ''
        };
        entry.TotalVAT += asNumber(r.totalVat || r.totalVAT || r.vat || r.VAT);
        entry.CostOfSales += asNumber(r.costOfSales || r.cost || r.cogs);
        entry.SaleTotal += asNumber(r.saleTotal || r.SaleTotal || r.turnOver || r.total || r.amount);
        entry.Qty += asNumber(r.qty || r.quantity || r.totalQty);
        grouped.set(key, entry);
      }
      return Array.from(grouped.values());
    },

    exportFranchiseSales() {
      this.$hapticFeedback.medium();

      try {
        // Convert table data to a worksheet
        let turnOver = this.stores.map(ft => {
          return {
            'Store': ft.store,
            'Cost Of Sales': ft.costOfSales,
            'Turn Over': ft.turnOver,
            'Net Profit': ft.profit
          }
        });

        if (turnOver.length === 0) {
          this.$hapticFeedback.error();
          Toast.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'No sales data available to export.',
            timer: 3000
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(turnOver);

        // Create a new workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "TableData");

        const ab1 = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab1, "Franchise Sales.xlsx");

        this.$hapticFeedback.success();
        Toast.fire({
          icon: 'success',
          title: 'Export Successful',
          text: 'Franchise sales data exported to Excel.',
          timer: 2000
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'Failed to export data to Excel. Please try again.',
          timer: 3000
        });
      }
    },
    exportFranchisePurchases() {
      this.$hapticFeedback.medium();

      try {
        // Convert purchase data to a worksheet using purchaseType as key
        const rows = (this.purchaseData || []).map(p => ({
          'Purchase Type': p.purchaseType,
          'Total Purchased Value': Number(p.amount) || 0
        }));

        if (rows.length === 0) {
          this.$hapticFeedback.error();
          Toast.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'No purchase data available to export.',
            timer: 3000
          });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(rows);

        // Create a new workbook and append the worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "TableData");

        const ab2 = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab2, "Franchise Purchases.xlsx");

        this.$hapticFeedback.success();
        Toast.fire({
          icon: 'success',
          title: 'Export Successful',
          text: 'Franchise purchases data exported to Excel.',
          timer: 2000
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'Failed to export data to Excel. Please try again.',
          timer: 3000
        });
      }
    },

    exportDashboardToExcel() {
      this.$hapticFeedback.medium();

      try {
        // Prepare data matching your dashboard sections
        let positivePosition = this.positivePosition;
        let negativePosition = this.negativePosition;
        let revenue = this.revenue;

        // Check if we have data to export
        if (!revenue || !positivePosition || !negativePosition) {
          this.$hapticFeedback.error();
          Toast.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'No dashboard data available to export.',
            timer: 3000
          });
          return;
        }

        const data = [
            ["Sales Stats","","","Positive Position","","","Negative Position"],
            ["Revenue (excl VAT)",revenue.salesTotal,"","Stock Value",positivePosition.stockValue,"","Supplier Balance",negativePosition.supplierBalance],
            ["Expenses",revenue.expenseValue,"","Debtors",positivePosition.debtorBalance,"","Output VAT",negativePosition.outPutVat],
            ["Net Profit",this.netProfit,"","Input VAT",positivePosition.inputVatTotal]
        ];

        // Create worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(data);

        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

        // Export the workbook using native Android opener on device, download elsewhere
        const ab3 = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        openLocalXlsx(ab3, "DashboardSummary.xlsx");

        this.$hapticFeedback.success();
        Toast.fire({
          icon: 'success',
          title: 'Export Successful',
          text: 'Dashboard summary exported to Excel.',
          timer: 2000
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'Failed to export dashboard to Excel. Please try again.',
          timer: 3000
        });
      }
    },

    async getTurnOverData() {
      try {
        const res = await this.$store.dispatch("getTurnOverData");
        this.stores = res;
        this.BarChartData = {
          labels: res.map(r => r.store),
          datasets: [
            {
              label: "Turn Over",
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                return value > 50000 ? '#28a745' : 
                       value > 20000 ? '#17a2b8' : 
                       value > 10000 ? '#007bff' : '#6c757d';
              },
              data: res.map(r => r.turnOver),
            },
            {
              label: "Cost of Sales",
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                return value > 30000 ? '#dc3545' : 
                       value > 15000 ? '#ffc107' : 
                       value > 5000 ? '#fd7e14' : '#6c757d';
              },
              data: res.map(r => r.costOfSales),
            },
            {
              label: "Net Profit",
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                return value > 20000 ? '#28a745' : 
                       value > 10000 ? '#17a2b8' : 
                       value > 5000 ? '#007bff' : '#6c757d';
              },
              data: res.map(r => r.profit),
            },
          ],
        };
      } catch (error) {
        console.error("Error fetching turnover data:", error);
        throw error;
      }
    },

    async getFranchiseRevenue() {
      this.revenue = await this.$store.dispatch("getFranchiseRevenue");
    },

    async getPositivePosition() {
      const data = await this.$store.dispatch("getPositivePosition");
      this.positivePosition = data;
    },

    async getNegativePosition() {
      const data = await this.$store.dispatch("getNegativePosition");
      this.negativePosition = data;
    },

    async getTopCategories() {
      const categories = await this.$store.dispatch("getSalesData", {
        route: "businessOverview/top10DepartmentSales",
        query: {},
      });
      this.chartData.labels = categories.map(c => c.department);
      this.chartData.datasets[0].data = categories.map(c => c.total);
    },
    async getFranchisePurchases() {
      try {
        const purchaseData = await this.$store.dispatch("getFranchisePurchases");
        this.purchaseData = purchaseData;
        this.Bar2ChartData = {
          labels: purchaseData.map(r => r.purchaseType),
          datasets: [
            {
              label: "Purchased Stock Value",
              backgroundColor: (context) => {
                const value = context.dataset.data[context.dataIndex];
                return value > 50000 ? '#28a745' : 
                       value > 20000 ? '#17a2b8' : 
                       value > 10000 ? '#007bff' : '#6c757d';
              },
              data: purchaseData.map(r => Number(r.amount) || 0),
            },
          ],
        };
      } catch (error) {
        console.error("Error fetching purchase data:", error);
        throw error;
      }
    },

    async printFranchiseTurnoverPDF() {
      this.$hapticFeedback.medium();
      try {
        await this.$store.dispatch("printFranchiseTurnoverPDF");
        this.$hapticFeedback.success();
        Toast.fire({
          icon: 'success',
          title: 'Print Successful',
          text: 'Franchise turnover PDF generated successfully.',
          timer: 2000
        });
      } catch (error) {
        console.error("Error printing PDF:", error);
        this.$hapticFeedback.error();
        Toast.fire({
          icon: 'error',
          title: 'Print Failed',
          text: 'Failed to generate PDF. Please try again.',
          timer: 3000
        });
      }
    },

    async openSalesReports() {
      this.$hapticFeedback.medium();
      Toast.fire({
        icon: 'info',
        title: 'Navigating',
        text: 'Opening Sales Dashboard...',
        timer: 1000
      });
      return this.$router.push({name: `SalesDashboard`});
    },

    async openStockReports() {
      this.$hapticFeedback.medium();
      Toast.fire({
        icon: 'info',
        title: 'Navigating',
        text: 'Opening Stock Dashboard...',
        timer: 1000
      });
      return this.$router.push({name: `StockDashboard`});
    },

    // Notification test actions (Electron/Capacitor/Web)
    notifyNow() {
      try {
        NotificationService.show('L⚡t‑ning', { body: 'Unified notification test' });
      } catch (e) { console.warn('notifyNow failed', e); }
    },
    scheduleNotify() {
      try {
        const id = Math.floor(Math.random() * 100000);
        const at = new Date(Date.now() + 5000);
        NotificationService.schedule('Reminder', { id, at, body: 'This fired after 5 seconds' });
      } catch (e) { console.warn('scheduleNotify failed', e); }
    },

    async enableNotifications() {
      try {
        const state = await NotificationService.ensurePermission();
        this.showAsk = state !== 'granted';
      } catch (e) { console.warn('enableNotifications failed', e); }
    },
    dismissAsk() { this.showAsk = false; },

    async printFranchiseTurnoverCSV() {
      return await this.$store.dispatch("printFranchiseTurnoverCSV");
    },
  },
};
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

.dashboard-header {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.dashboard-header h2 {
  font-weight: 600;
  font-size: 24px;
  color: #2f4861;
  margin: 0;
}

.dashboard-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dashboard-actions .btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: var(--radius-button);
  font-size: 14px;
  font-weight: 500;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dashboard-actions .refresh {
  background-color: var(--accent-primary);
}

.dashboard-actions .refresh:hover {
  background-color: var(--accent-primary-hover);
}

.dashboard-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

/* Legacy KPI card styles - kept for backward compatibility */
.kpi-card {
  flex: 1 1 30%;
  background: var(--card-bg);
  border: var(--card-border, 1px solid var(--grid-lines));
  border-radius: var(--radius-card);
  padding: var(--space-4, 16px);
  min-width: 200px;
  text-align: center;
  box-shadow: var(--shadow, 0 3px 10px rgba(0,0,0,0.08));
  transition: var(--transition);
  cursor: pointer;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.kpi-card h5 {
  font-size: 15px;
  color: var(--text-secondary);
}

.kpi-card p {
  font-size: 20px;
  font-weight: bold;
  color: var(--text-primary);
}

.kpi-card small {
  font-size: 13px;
  color: var(--text-secondary);
}

.financial-position {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 30px;
}

.position-block {
  flex: 1 1 45%;
  background: var(--card-bg);
  border-radius: var(--radius-card);
  padding: 16px;
  min-width: 250px;
  box-shadow: var(--shadow, 0 3px 10px rgba(0,0,0,0.05));
  transition: var(--transition);
}

.position-block:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
}

.position-block h5 {
  font-size: 16px;
  margin-bottom: 12px;
  color: var(--text-primary);
  font-weight: 600;
}

.position-block ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.position-block li {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 10px;
  border-radius: var(--radius-card);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  background-color: var(--card-bg);
  border: var(--card-border, 1px solid var(--grid-lines));
  box-shadow: var(--shadow, 0 1px 3px rgba(0,0,0,0.05));
}

.position-block li:hover {
  filter: brightness(1.02);
  box-shadow: 0 2px 5px rgba(0,0,0,0.08);
  transform: translateY(-1px);
}

.position-block li:active {
  filter: brightness(0.98);
  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

/* Touch ripple effect for position items */
.position-block li::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: color-mix(in oklab, var(--accent-primary) 30%, transparent);
  opacity: 0;
  border-radius: 100%;
  transform: scale(1, 1) translate(-50%, -50%);
  transform-origin: 50% 50%;
}

.position-block li:active::after {
  opacity: 1;
  animation: ripple 0.4s ease-out;
}

.position-block label {
  font-weight: 500;
  color: var(--text-secondary);
}

.position-block span {
  font-weight: bold;
  color: var(--text-primary);
}

.charts-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.chart-card {
  flex: 1 1 30%;
  background: var(--card-bg);
  border: var(--card-border, 1px solid var(--grid-lines));
  border-radius: var(--radius-card);
  padding: var(--space-4, 16px);
  min-width: 300px;
  box-shadow: var(--shadow, 0 3px 10px rgba(0,0,0,0.08));
  transition: var(--transition);
  margin-bottom: var(--space-4, 16px);
  overflow: hidden;
}

.chart-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Chart container to ensure proper sizing */
.chart-card canvas {
  max-width: 100%;
  height: auto !important;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--accent-primary);
}

.chart-actions .btn {
  font-size: 12px;
  margin-left: 5px;
  background: var(--accent-primary);
  color: #fff;
  border: none;
  padding: 6px 10px;
  border-radius: var(--radius-button);
  cursor: pointer;
  transition: var(--transition);
}

.chart-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.chart-actions .excel {
  background: var(--success);
}

.chart-actions .view {
  background: var(--accent-primary);
}

.interactive-btn {
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.interactive-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

/* Active state for touch devices */
.interactive-btn:active {
  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  background: color-mix(in oklab, var(--text-primary) 10%, transparent);
}

/* Touch ripple effect */
.interactive-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: color-mix(in oklab, var(--text-inverse) 50%, transparent);
  opacity: 0;
  border-radius: 100%;
  transform: scale(1, 1) translate(-50%, -50%);
  transform-origin: 50% 50%;
}

.interactive-btn:active::after {
  opacity: 1;
  animation: ripple 0.4s ease-out;
}

@keyframes ripple {
  0% {
    transform: scale(0, 0) translate(-50%, -50%);
    opacity: 0.5;
  }
  100% {
    transform: scale(20, 20) translate(-50%, -50%);
    opacity: 0;
  }
}

.no-data {
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  padding: 20px;
}

/* Android notification permission banner */
.notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--card-bg);
  border: var(--card-border, 1px solid var(--grid-lines));
  border-radius: var(--radius-card);
  padding: 10px 12px;
  margin: 10px 0 16px;
  box-shadow: var(--shadow, 0 1px 3px rgba(0,0,0,0.05));
}
.notice-text { color: var(--text-primary); font-size: 14px; }
.notice-actions { display: flex; gap: 8px; }
.notice .btn { background: var(--accent-primary); color: #fff; border: none; padding: 6px 10px; border-radius: var(--radius-button); }
.notice .btn:hover { filter: brightness(1.05); }

.dashboard {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scrollable-dashboard {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  max-height: calc(100vh - 50px); /* Adjust based on header height */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  scroll-behavior: smooth; /* Smooth scrolling on modern browsers */
  overscroll-behavior: contain; /* Prevent scroll chaining */
}

/* Improve scrollbar appearance */
.scrollable-dashboard::-webkit-scrollbar {
  width: 8px;
}

.scrollable-dashboard::-webkit-scrollbar-track {
  background: rgba(0,0,0,0.05);
  border-radius: 4px;
}

.scrollable-dashboard::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.2);
  border-radius: 4px;
}

.scrollable-dashboard::-webkit-scrollbar-thumb:hover {
  background: rgba(0,0,0,0.3);
}

/* Color classes (tokenized) */
.text-success { color: var(--success) !important; }
.text-warning { color: var(--warning) !important; }
.text-danger { color: var(--danger) !important; }
.text-info { color: var(--accent-secondary) !important; }
.text-primary { color: var(--accent-primary) !important; }
.text-secondary { color: var(--text-secondary) !important; }

/* Responsive adjustments */
@media (max-width: 768px) {
  .dashboard {
    padding: 15px;
  }

  .dashboard-header {
    margin-bottom: 15px;
  }

  .dashboard-header h2 {
    font-size: 20px;
  }

  .dashboard-actions .btn {
    padding: 6px 10px;
  }

  .chart-actions .btn {
    padding: 5px 8px;
    font-size: 12px;
  }

  .dashboard-kpis {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 12px;
  }

  .financial-position {
    gap: 12px;
  }

  .position-block {
    flex: 1 1 100%;
    min-width: unset;
    padding: 12px;
  }

  .position-block h5 {
    font-size: 15px;
    margin-bottom: 10px;
  }

  .position-block li {
    margin-bottom: 8px;
    padding: 6px;
  }

  .charts-container {
    flex-direction: column;
    gap: 15px;
  }

  .chart-card {
    min-width: unset;
    padding: 12px;
  }

  .chart-header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    margin-bottom: 12px;
  }

  .chart-title {
    font-size: 15px;
    grid-column: 1;
  }

  .chart-actions {
    grid-column: 2;
    display: flex;
    gap: 5px;
  }

  .chart-actions .btn {
    padding: 5px 8px;
    font-size: 11px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .dashboard {
    padding: 10px;
  }

  .dashboard-header {
    margin-bottom: 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .dashboard-header h2 {
    font-size: 18px;
    width: 100%;
    text-align: center;
  }

  .dashboard-actions {
    width: 100%;
    justify-content: center;
  }

  .dashboard-actions .btn {
    padding: 10px;
    border-radius: 8px;
  }

  .dashboard-actions .btn-text {
    display: none;
  }

  .dashboard-actions .btn svg {
    font-size: 16px;
    margin: 0;
  }

  .dashboard-kpis {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 10px;
  }

  .kpi-card {
    padding: 12px;
  }

  .kpi-card h5 {
    font-size: 14px;
  }

  .kpi-card p {
    font-size: 18px;
  }

  .financial-position {
    gap: 10px;
  }

  .position-block {
    padding: 10px;
  }

  .position-block h5 {
    font-size: 14px;
  }

  .position-block label {
    font-size: 13px;
  }

  .position-block span {
    font-size: 13px;
  }

  .chart-card {
    padding: 10px;
  }

  .chart-header {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .chart-title {
    font-size: 14px;
  }

  .chart-actions {
    grid-column: 1;
    justify-content: flex-start;
  }
}
</style>
